import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { ApplicationState } from '../../store';
import { SurveyTemplateDataModel } from '../../store/Models/Surveys/SurveyDataModel';
import * as TemplateManagerStateController from '../../store/StateControllers/Survey/TemplateManagerStateController';
import * as LoginStateController from '../../store/StateControllers/LoginStateController';
import { SurveyTemplateManagerData } from '../../store/StateControllers/Survey/TemplateManagerStateController';
import { ScrollableContent, TopActionButtons, TopActionsAndScrollableContentContainer } from '../core/TopActionsAndScrollableContentContainer';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import Layout from './../Layout';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../core/ToxnotReactModal';
import React from 'react';

import "./styles.css";
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../store/Models/UserContext';

// UI component for the Survey Template Manager served from "Survey/ManagerTemplates".

// ======================= Types ==============================================================
type SurveyTemplateManagerProps = ToxnotComponentProperties & RouteComponentProps & typeof TemplateManagerStateController.actionCreators & SurveyTemplateManagerData;


class SurveyTemplateManager extends ToxnotSignInRequiredComponent<SurveyTemplateManagerProps> {
    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }
 
    public componentDidMount() {
        this.props.loadSurveyTemplates();
    }

    protected renderConcreteComponent(): JSX.Element | null {
        if (!this.props.dataLoaded) {
            return (
                <Layout>
                    <div className="tn-section">
                        <div className="text-center">
                            <LoadingSpinner size={LoadingSpinnerSize.Large}/>
                         </div>
                    </div>    
                </Layout>    
            )
        }

        if (this.props.showErrorMessage) {
            return (
                <Layout>
                    <div className="tn-section">
                        <p>{this.localizeString("Sorry, there was an error accessing the service. Please try again later.")}</p>
                    </div>    
                </Layout>    
            )
        }

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }} modalsRender={this.renderModals}>
                {/**Top Navbar*/}
                <TopActionsAndScrollableContentContainer customClassName="template-manager-root" shouldMakeTnSection={true} tnSectionIsRaw={false}>
                    <div className="tn-section-row tn-section-heading-row"><div className="tn-section-col-12"><h1>{this.localizeString("Manage Survey Templates")}</h1></div></div>
                    <TopActionButtons formatButtons={true}>
                        <button type="button" className="btn btn-toxnot-primary" onClick={e => $("#add-new-template-modal").modal("show")}><span className="glyphicon glyphicon-plus"></span> {this.localizeString("New Template")}</button>
                        <button
                            type="button"
                            className="btn btn-toxnot-danger"
                            onClick={e => this.deleteTemplatesButton_click()}
                            disabled={!this.props.selectedTemplateIds || this.props.selectedTemplateIds.length === 0}
                        >
                            <span className="glyphicon glyphicon-remove"></span> {this.localizeString("Templates")}
                        </button>
                    </TopActionButtons>
                    <ScrollableContent>
                        <ToxnotReactTable<SurveyTemplateDataModel>
                            key={this.props.templates.map(t => t.templateId).join(",")}
                            headerRow={[{ value: this.localizeString("Name") }, { value: this.localizeString("Last Modified") }, { value: this.localizeString("Last Modified By") }]}
                            allRowModels={this.props.templates}
                            getRowForTable={this.getRowForTemplate}
                            localizer={this.props.appServices}
                            stickyHeader={true}
                            selectingRowOptions={{
                                rowModelGetId: (model: SurveyTemplateDataModel) => model.templateId ?? "",
                                onRowSelected: (ids: {[id: string]: string}) => {
                                    this.props.setData({selectedTemplateIds: Object.keys(ids)});
                                }
                            }}
                            enablePaging={false}
                            />
                    </ScrollableContent>
                </TopActionsAndScrollableContentContainer>
            </Layout>  
        );
    }

    // Helper Method for ToxnotReactTable. Retrives a row for the table.
    private getRowForTemplate = (data: SurveyTemplateDataModel, rowIndex: number): TableRow => {
        if (data.templateId === null) {
            throw new Error("unexpected null template id");
        }

        return {
            cols: [
                <div>
                    <Link to={"/Survey/Template/" + encodeURIComponent(data.templateId)}>{data.name}</Link>
                    {this.props.defaultSurveyTemplateId === data.templateId && (
                        <span className="orange-label"> Default</span>
                    )}
                </div>,
                <span>{data.lastModified ? new Date(data.lastModified).toLocaleString() : ''}</span>,
                <span>
                    {data.lastModifiedBy ? (
                        data.lastModifiedByEmail ?
                            `${data.lastModifiedBy} (${data.lastModifiedByEmail})` :
                            data.lastModifiedBy
                    ) : ''}
                </span>
            ]
        };
    }

    private renderModals = () => {
        return (
            <React.Fragment>
                <div id="add-new-template-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Add New Template")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>{this.localizeString("Template Name")}</label>
                                    <input type="string" className="form-control" id="new-template-name" required/>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" className="btn btn-toxnot-primary" onClick={e => this.addNewTemplateConfirmButton_click()} data-dismiss="modal">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <ToxnotReactModal modalId="confirm-template-deletion-modal" localizedTitle={this.localizeString("Confirm Template Deletion")}>
                    <ToxnotReactModalBody>
                        <p>{this.localizeString("Are you sure you want to delete the selected survey templates? This action cannot be undone.")}</p>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                        <button className="btn btn-default" onClick={this.cancelDeleteButton_click}>{this.localizeString("Cancel")}</button>
                        <button className="btn btn-danger" onClick={this.confirmDeleteButton_click}>{this.localizeString("Delete")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
            </React.Fragment>
        );
    }

    // Event handler for when the add new template button in the modal is clicked.
    private addNewTemplateConfirmButton_click = () => {
        const newTemplateName = $("#new-template-name").val()!.toString().trim();

        if (newTemplateName.length <= 0) {
            alert(this.localizeString("Please supply a name for the template."));
            return;
        }

        this.props.addNewTemplateAndRedirect(newTemplateName, this.props.history);
    }

    private deleteTemplatesButton_click = () => {
        if (this.props.selectedTemplateIds && this.props.selectedTemplateIds.length > 0) {
            $("#confirm-template-deletion-modal").modal("show");
        }
    }

    private cancelDeleteButton_click = () => {
        $("#confirm-template-deletion-modal").modal("hide");
    }

    private confirmDeleteButton_click = () => {
        this.props.removeTemplate(this.props.selectedTemplateIds);
        $("#confirm-template-deletion-modal").modal("hide");
    }

}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyTemplateManagerState , appServices: state.appServices }),
    TemplateManagerStateController.actionCreators
)(withRouter(SurveyTemplateManager) as any);
